/**
 * @author Simeon Artamonov <simeon@cross-works.co.uk>
 * @company Cross Works Ltd
 */
import { createRouter, createWebHistory } from 'vue-router'
import { useAppStateStore } from '../stores/appState'

const routes = [
  {
    path: '/',
    name: 'Home',
    component: () => import('../views/HomePage.vue')
  },
  {
    path: '/about',
    name: 'About',
    component: () => import('../views/About.vue')
  },
  {
    path: '/contact',
    name: 'Contact',
    component: () => import('../views/Contact.vue')
  },
  {
    path: '/news',
    name: 'News',
    component: () => import('../views/NewsListing.vue')
  },
  {
    path: '/news/:page(\\d+)',
    name: 'NewsPaginated',
    component: () => import('../views/NewsListing.vue'),
    props: route => ({ page: parseInt(route.params.page) })
  },
  {
    path: '/news/:slug([a-zA-Z][a-zA-Z0-9-_]*)',
    name: 'SingleNews',
    component: () => import('../views/NewsSingle.vue'),
    props: true
  },
  {
    path: '/design',
    name: 'Design',
    component: () => import('../views/HomePage.vue')
  },
  {
    path: '/technology',
    name: 'Technology',
    component: () => import('../views/HomePage.vue')
  },
  {
    path: '/design-brochure',
    name: 'DesignBrochureDownload',
    component: () => import('../views/Download.vue')
  },
  {
    path: '/newtashkent',
    name: 'NewtashkentDownload',
    component: () => import('../views/Download.vue')
  },
  {
    path: '/apps-brochure',
    name: 'AppsBrochureDownload',
    component: () => import('../views/Download.vue')
  },
  {
    path: '/webdesign-brochure',
    name: 'WebdesignBrochureDownload',
    component: () => import('../views/Download.vue')
  },
  {
    path: '/:slug',
    name: 'Page',
    component: () => import('../views/Page.vue')
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('../views/NotFound.vue')
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (to.hash) {
      const scrollInPanel = ['/design', '/technology'].includes(to.path)

      if (scrollInPanel) {
        return new Promise((resolve) => {
          const appStateStore = useAppStateStore()
          
          // Helper function to scroll to the hash target
          const scrollToHash = () => {
            const side = to.path === '/design' ? 'left' : 'right'
            const panelSelector = `.side-panel.${side}.active .panel-content`
            const panel = document.querySelector(panelSelector)
            const target = document.querySelector(to.hash)

            if (panel && target) {
              // Calculate the offset relative to the panel content
              const panelRect = panel.getBoundingClientRect()
              const targetRect = target.getBoundingClientRect()
              const scrollTop = panel.scrollTop + (targetRect.top - panelRect.top)
              
              panel.scrollTo({
                top: scrollTop,
                behavior: 'smooth'
              })
            }
          }
          
          // Check if panel is already fully open
          if (appStateStore.currentState === 'panel-open') {
            // Panel is open, but give a small delay for content to settle
            setTimeout(() => {
              scrollToHash()
              resolve(false) // prevent native scroll
            }, 100)
            return
          }
          
          // Panel is not open yet, wait for it to become ready
          let attempts = 0
          const maxAttempts = 20 // Max 2 seconds
          
          const checkPanelReady = () => {
            attempts++
            
            if (appStateStore.currentState === 'panel-open') {
              // Panel is now open, wait a bit more for content positioning
              setTimeout(() => {
                scrollToHash()
                resolve(false) // prevent native scroll
              }, 200)
            } else if (attempts >= maxAttempts) {
              // Timeout, just resolve without scrolling
              console.warn('Panel hash scroll timeout - panel not ready in time')
              resolve(false)
            } else {
              // Check again in 100ms
              setTimeout(checkPanelReady, 100)
            }
          }
          
          // Start checking
          checkPanelReady()
        })
      }

      return {
        el: to.hash,
        behavior: 'smooth'
      }
    }

    if (savedPosition) {
      return savedPosition
    }

    return { top: 0 }
  }
})

export default router
